import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getCitaAsistencia, useAgentTrainingAppointments } from "@/hooks/useAgentTrainingAppointments";
import {
  esAgenteExterno,
  pasoDocumentos,
  requisitoDesdeFila,
  requisitosQueAplican,
  type RequisitoDocumento,
} from "@/lib/agente-documentos-requeridos";

export interface OnboardingStep {
  id: 'basic' | 'address' | 'fiscal' | 'documents' | 'bank-accounts' | 'training';
  label: string;
  isComplete: boolean;
  hasPartialData: boolean;
  hasCancelledData?: boolean;
  /** Texto de estado del paso cuando no basta el check (ej. cuenta bancaria en revisión). */
  detalle?: string;
  /** El paso ya tiene captura aunque aún no esté validado (para gates que piden "existe"). */
  tieneCaptura?: boolean;
}

interface OnboardingStatus {
  steps: OnboardingStep[];
  completedCount: number;
  totalSteps: number;
  percentage: number;
  isLoading: boolean;
  hasTrainingComplete: boolean;
  hasBasicIdentityComplete: boolean;
  canAccessComisiones: boolean;
  missingForComisiones: string[];
  missingByStep: Record<string, string[]>;
  /**
   * El agente está ligado a una inmobiliaria: ella factura y le paga, así que su
   * expediente no incluye información fiscal, cuenta bancaria ni CSF.
   */
  esDependiente: boolean;
}

export function useAgentOnboardingStatus(personaId: number | null | undefined): OnboardingStatus {
  const { data: hasInmobiliaria, isLoading: loadingInmo } = useQuery({
    queryKey: ['agent-onboarding-inmo', personaId],
    queryFn: async () => {
      if (!personaId) return false;
      const { data } = await supabase
        .from('entidades_relacionadas')
        .select('id')
        .eq('id_persona', personaId)
        .eq('id_tipo_entidad', 19)
        .eq('activo', true)
        .not('id_persona_duena_lead', 'is', null)
        .limit(1);
      return (data && data.length > 0) || false;
    },
    enabled: !!personaId,
  });

  const { data: persona, isLoading: loadingPersona } = useQuery({
    queryKey: ['agent-onboarding-persona', personaId],
    queryFn: async () => {
      if (!personaId) return null;
      const { data, error } = await supabase
        .from('personas')
        .select('nombre_legal, email, telefono, direccion_calle, direccion_num_ext, direccion_colonia, direccion_codigo_postal, direccion_id_pais, direccion_id_estado, direccion_id_municipio, rfc, regimen, uso_cfdi, direccion_fiscal_calle, direccion_fiscal_colonia, direccion_fiscal_codigo_postal, direccion_fiscal_id_pais, direccion_fiscal_id_estado, direccion_fiscal_id_municipio')
        .eq('id', personaId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!personaId,
  });

  // Requisitos del paso "Documentos" (Sistema > Documentos requeridos): activos, que
  // cuentan para el perfil. Tabla ausente (migración pendiente) = sin requisitos, como la EF.
  const { data: requisitos = [], isLoading: loadingRequisitos } = useQuery({
    queryKey: ['agent-onboarding-requisitos'],
    queryFn: async (): Promise<RequisitoDocumento[]> => {
      // La tabla es nueva y aún no está en los tipos generados de Supabase.
      const { data, error } = await (supabase as any)
        .from('documentos_requeridos')
        .select('clave, nombre, exige_validado, documentos_requeridos_tipos(id_tipo_documento), documentos_requeridos_perfiles(perfil)')
        .eq('activo', true)
        .eq('cuenta_para_perfil', true)
        .order('orden')
        .order('id');
      if (error) {
        if (error.code === '42P01' || error.code === 'PGRST205') return [];
        throw error;
      }
      return ((data || []) as Record<string, unknown>[])
        .map(requisitoDesdeFila)
        .filter((r): r is RequisitoDocumento => r !== null);
    },
    staleTime: 5 * 60 * 1000,
  });

  // Interno (9) o externo (3): decide qué requisitos aplican. Mismo rol efectivo que la EF.
  const { data: esExterno = false, isLoading: loadingRol } = useQuery({
    queryKey: ['agent-onboarding-rol', personaId],
    queryFn: async () => {
      if (!personaId) return false;
      const { data: usuario } = await supabase
        .from('usuarios')
        .select('email, rol_id')
        .eq('id_persona', personaId)
        .maybeSingle();
      if (!usuario) return false;
      const { data: roles } = await supabase
        .from('user_roles')
        .select('rol_id')
        .eq('email', usuario.email)
        .eq('activo', true);
      return esAgenteExterno(
        usuario.rol_id,
        ((roles || []) as { rol_id: number }[]).map((r) => r.rol_id),
      );
    },
    enabled: !!personaId,
  });

  const tiposRequisitos = [...new Set(requisitos.flatMap((r) => r.tipos))].sort((a, b) => a - b);
  const { data: documentos = [], isLoading: loadingDocs } = useQuery({
    queryKey: ['agent-onboarding-docs', personaId, tiposRequisitos.join(',')],
    queryFn: async () => {
      if (!personaId) return [];
      const { data, error } = await supabase
        .from('documentos')
        .select('id_tipo_documento, id_estatus_verificacion')
        .eq('id_persona', personaId)
        .eq('activo', true)
        .in('id_tipo_documento', [...new Set([2, 3, 4, 6, 48, 63, ...tiposRequisitos])]);
      if (error) throw error;
      return data || [];
    },
    enabled: !!personaId && !loadingRequisitos,
  });

  // Carta de cumplimiento firmada vía Mifiel (fallback si el webhook no creó el doc tipo 48)
  const { data: cartaFirmada = false, isLoading: loadingCarta } = useQuery({
    queryKey: ['agent-onboarding-carta-firmada', personaId],
    queryFn: async () => {
      if (!personaId) return false;
      const { data, error } = await supabase
        .from('firmas_digitales')
        .select('id')
        .eq('tipo_documento', 'carta_acuerdos')
        .eq('referencia_id', personaId)
        .eq('estado', 'completado')
        .limit(1);
      if (error) throw error;
      return !!(data && data.length > 0);
    },
    enabled: !!personaId,
  });

  const { data: cuentas = [], isLoading: loadingCuentas } = useQuery({
    queryKey: ['agent-onboarding-bank', personaId],
    queryFn: async () => {
      if (!personaId) return [];
      // `motivo_rechazo` puede no existir todavía en la BD (42703): se reintenta sin ella.
      const consulta = (cols: string) =>
        supabase
          .from('cuentas_bancarias')
          .select(cols)
          .eq('id_persona', personaId)
          .eq('activo', true)
          .order('id', { ascending: false });
      let res: { data: any; error: any } = await consulta('id, id_estatus_verificacion, motivo_rechazo');
      if (res.error?.code === '42703') res = await consulta('id, id_estatus_verificacion');
      if (res.error) throw res.error;
      return (res.data || []) as Array<{ id: number; id_estatus_verificacion: number | null; motivo_rechazo?: string | null }>;
    },
    enabled: !!personaId,
  });

  const { appointments: citasCapacitacion = [], isLoading: loadingCitas } = useAgentTrainingAppointments(personaId);

  const isLoading =
    loadingInmo || loadingPersona || loadingDocs || loadingCuentas || loadingCitas || loadingCarta ||
    loadingRequisitos || loadingRol;

  // Paso "Documentos": null si no hay requisitos que le apliquen (el paso no existe).
  const pasoDocs = pasoDocumentos(
    requisitosQueAplican(requisitos, esExterno),
    documentos as { id_tipo_documento: number; id_estatus_verificacion: number | null }[],
    cartaFirmada,
  );
  const stepDocumentos: OnboardingStep[] = pasoDocs
    ? [{ id: 'documents', label: 'Documentos', isComplete: pasoDocs.completo, hasPartialData: pasoDocs.parcial }]
    : [];

  // La capacitación se da por cumplida solo con asistencia registrada: una cita
  // "Confirmada" (id_estatus_cita = 3) todavía no acredita el paso.
  const activeCitas = citasCapacitacion.filter((c: any) => c.activo);
  const trainingComplete = activeCitas.some((c: any) => getCitaAsistencia(c) === 'asistio');
  const trainingPartial = !trainingComplete && activeCitas.some((c: any) => getCitaAsistencia(c) === 'pendiente');
  const trainingCancelled = !trainingComplete && !trainingPartial && activeCitas.some((c: any) => c.estatus === 'cancelada' || c.estatus === 'no_asistio');

  const trainingMissing: string[] = [];
  if (!trainingComplete) {
    const hasNoShow = activeCitas.some((c: any) => c.estatus === 'no_asistio');
    if (hasNoShow) trainingMissing.push('Reagendar capacitación');
    else if (trainingPartial) trainingMissing.push('Cita programada (pendiente de asistencia)');
    else trainingMissing.push('Agendar capacitación');
  }

  if (hasInmobiliaria && !isLoading) {
    const basicComplete = !!(persona?.nombre_legal && persona?.email && persona?.telefono);
    const basicPartial = !basicComplete && !!(persona?.nombre_legal || persona?.email || persona?.telefono);

    const addressComplete = !!(
      persona?.direccion_calle &&
      persona?.direccion_num_ext &&
      persona?.direccion_colonia &&
      persona?.direccion_codigo_postal &&
      persona?.direccion_id_pais &&
      persona?.direccion_id_estado &&
      persona?.direccion_id_municipio
    );
    const addressPartial = !addressComplete && !!(persona?.direccion_calle || persona?.direccion_num_ext || persona?.direccion_colonia || persona?.direccion_codigo_postal);

    const docTypes = new Set(documentos.map((d: any) => d.id_tipo_documento));
    // 63 = INE completo (un solo PDF); 2+3 = formato anterior por separado.
    const hasINE = docTypes.has(63) || (docTypes.has(2) && docTypes.has(3));
    const hasPasaporte = docTypes.has(4);
    const hasIdentityDoc = hasINE || hasPasaporte;

    // El paso "Identidad" solo captura datos personales + dirección; la INE/pasaporte
    // se sube desde el Expediente y se reporta en la sección "Documentos".
    const identityComplete = basicComplete && addressComplete;
    const identityPartial = !identityComplete && (basicPartial || basicComplete || addressPartial || addressComplete);

    const basicMissing: string[] = [];
    if (!persona?.nombre_legal) basicMissing.push('Nombre completo');
    if (!persona?.email) basicMissing.push('Correo electrónico');
    if (!persona?.telefono) basicMissing.push('Teléfono');
    if (!persona?.direccion_calle) basicMissing.push('Dirección (calle)');
    if (!persona?.direccion_num_ext) basicMissing.push('Num. exterior');
    if (!persona?.direccion_colonia) basicMissing.push('Colonia');
    if (!persona?.direccion_codigo_postal) basicMissing.push('Código postal');
    if (!persona?.direccion_id_pais) basicMissing.push('País');
    if (!persona?.direccion_id_estado) basicMissing.push('Estado');
    if (!persona?.direccion_id_municipio) basicMissing.push('Municipio');

    const documentsMissing: string[] = [];
    if (!hasIdentityDoc) documentsMissing.push('INE o Pasaporte');

    // Fiscal y cuenta bancaria las administra la inmobiliaria: para el dependiente no
    // son pendientes suyos, así que cuentan como completos y en el perfil se muestran
    // en solo lectura.
    const inmoSteps: OnboardingStep[] = [
      { id: 'basic', label: 'Identidad', isComplete: identityComplete, hasPartialData: identityPartial },
      ...stepDocumentos,
      { id: 'fiscal', label: 'Información fiscal', isComplete: true, hasPartialData: false },
      { id: 'bank-accounts', label: 'Cuenta bancaria', isComplete: true, hasPartialData: false },
      { id: 'training', label: 'Capacitación', isComplete: trainingComplete, hasPartialData: trainingPartial, hasCancelledData: trainingCancelled },
    ];

    const inmoCompleted = inmoSteps.filter(s => s.isComplete).length;
    return {
      steps: inmoSteps,
      completedCount: inmoCompleted,
      totalSteps: inmoSteps.length,
      percentage: Math.round((inmoCompleted / inmoSteps.length) * 100),
      isLoading: false,
      hasTrainingComplete: trainingComplete,
      hasBasicIdentityComplete: identityComplete,
      canAccessComisiones: true,
      missingForComisiones: [],
      missingByStep: {
        basic: basicMissing,
        fiscal: [],
        'bank-accounts': [],
        training: trainingMissing,
        documents: pasoDocs ? pasoDocs.faltantes : documentsMissing,
      },
      esDependiente: true,
    };
  }

  const basicComplete = !!(persona?.nombre_legal && persona?.email && persona?.telefono);
  const basicPartial = !basicComplete && !!(persona?.nombre_legal || persona?.email || persona?.telefono);

  const addressComplete = !!(
    persona?.direccion_calle &&
    persona?.direccion_num_ext &&
    persona?.direccion_colonia &&
    persona?.direccion_codigo_postal &&
    persona?.direccion_id_pais &&
    persona?.direccion_id_estado &&
    persona?.direccion_id_municipio
  );
  const addressPartial = !addressComplete && !!(persona?.direccion_calle || persona?.direccion_num_ext || persona?.direccion_colonia || persona?.direccion_codigo_postal);

  const fiscalComplete = !!(
    persona?.rfc &&
    persona?.regimen &&
    persona?.uso_cfdi &&
    persona?.direccion_fiscal_calle &&
    persona?.direccion_fiscal_colonia &&
    persona?.direccion_fiscal_codigo_postal &&
    persona?.direccion_fiscal_id_pais &&
    persona?.direccion_fiscal_id_estado &&
    persona?.direccion_fiscal_id_municipio
  );
  const fiscalPartial = !fiscalComplete && !!(persona?.rfc || persona?.regimen || persona?.uso_cfdi);

  const docTypes = new Set(documentos.map((d: any) => d.id_tipo_documento));
  // 63 = INE completo (frente y reverso en un solo PDF), el formato vigente desde
  // 2026-08. Los tipos 2+3 se conservan para quienes ya lo tenían por separado.
  const hasINE = docTypes.has(63) || (docTypes.has(2) && docTypes.has(3));
  const hasPasaporte = docTypes.has(4);
  const hasIdentityDoc = hasINE || hasPasaporte;
  const hasCartaCumplimiento = docTypes.has(48) || cartaFirmada;
  const documentsComplete = hasIdentityDoc && hasCartaCumplimiento;

  // Cuenta bancaria: "completa" solo con una cuenta validada (2). Subirla no basta: queda
  // "cargada, pendiente de validar" (1) o "rechazada" (3). Los gates de comisiones siguen
  // pidiendo que EXISTA una cuenta (`bankTieneCuenta`), como antes.
  const bankTieneCuenta = cuentas.length > 0;
  const bankComplete = cuentas.some((c) => c.id_estatus_verificacion === 2);
  const bankRechazada = !bankComplete && cuentas.some((c) => c.id_estatus_verificacion === 3) && !cuentas.some((c) => c.id_estatus_verificacion !== 3);
  const bankPendiente = bankTieneCuenta && !bankComplete && !bankRechazada;
  const bankMotivo = cuentas.find((c) => c.id_estatus_verificacion === 3 && c.motivo_rechazo)?.motivo_rechazo ?? null;
  const bankDetalle = bankComplete
    ? undefined
    : bankRechazada
    ? `Rechazada${bankMotivo ? `: ${bankMotivo}` : ''}`
    : bankPendiente
    ? 'Cargada, pendiente de validar'
    : undefined;

  // El paso "Identidad" ya solo captura datos personales + dirección (INE/pasaporte
  // y carta se suben desde el Expediente). El badge del paso refleja datos+dirección;
  // los documentos siguen exigiéndose aparte para habilitar comisiones (ver abajo).
  const basicStageComplete = basicComplete && addressComplete;
  const basicStagePartial = !basicStageComplete && (basicPartial || basicComplete || addressPartial || addressComplete);

  const constanciaApproved = documentos.some((d: any) => d.id_tipo_documento === 6 && d.id_estatus_verificacion === 2);
  const constanciaExists = docTypes.has(6);
  // La CSF es la fuente de los datos fiscales: si se extrajo y quedó validada
  // (estatus 2), la sección fiscal se da por válida/completa directamente, sin
  // exigir el resto de campos (uso_cfdi, IDs país/estado/municipio fiscal) que
  // la constancia no aporta al extraer.
  const fiscalStageComplete = constanciaApproved || fiscalComplete;
  const fiscalStagePartial = !fiscalStageComplete && (fiscalPartial || fiscalComplete || constanciaExists);

  const basicMissing: string[] = [];
  if (!persona?.nombre_legal) basicMissing.push('Nombre completo');
  if (!persona?.email) basicMissing.push('Correo electrónico');
  if (!persona?.telefono) basicMissing.push('Teléfono');
  if (!persona?.direccion_calle) basicMissing.push('Dirección (calle)');
  if (!persona?.direccion_num_ext) basicMissing.push('Num. exterior');
  if (!persona?.direccion_colonia) basicMissing.push('Colonia');
  if (!persona?.direccion_codigo_postal) basicMissing.push('Código postal');
  if (!persona?.direccion_id_pais) basicMissing.push('País');
  if (!persona?.direccion_id_estado) basicMissing.push('Estado');
  if (!persona?.direccion_id_municipio) basicMissing.push('Municipio');

  const fiscalMissing: string[] = [];
  if (!persona?.rfc) fiscalMissing.push('RFC');
  if (!persona?.regimen) fiscalMissing.push('Régimen fiscal');
  if (!persona?.uso_cfdi) fiscalMissing.push('Uso CFDI');
  if (!persona?.direccion_fiscal_calle) fiscalMissing.push('Calle fiscal');
  if (!persona?.direccion_fiscal_colonia) fiscalMissing.push('Colonia fiscal');
  if (!persona?.direccion_fiscal_codigo_postal) fiscalMissing.push('C.P. fiscal');
  if (!persona?.direccion_fiscal_id_pais) fiscalMissing.push('País fiscal');
  if (!persona?.direccion_fiscal_id_estado) fiscalMissing.push('Estado fiscal');
  if (!persona?.direccion_fiscal_id_municipio) fiscalMissing.push('Municipio fiscal');
  if (!constanciaExists) fiscalMissing.push('Constancia de situación fiscal');
  else if (!constanciaApproved) fiscalMissing.push('Constancia pendiente de aprobación');

  const bankMissing: string[] = [];
  if (!bankTieneCuenta) bankMissing.push('Cuenta bancaria');
  else if (bankRechazada) bankMissing.push(bankDetalle ?? 'Rechazada');
  else if (bankPendiente) bankMissing.push('Cuenta bancaria pendiente de validar');

  // Documentos del expediente (identidad + carta) — se capturan aparte del paso
  // "Identidad" pero siguen siendo requisito para habilitar comisiones.
  const documentsMissing: string[] = [];
  if (!hasIdentityDoc) documentsMissing.push('INE o Pasaporte');
  if (!hasCartaCumplimiento) documentsMissing.push('Carta de comercialización');

  const missingByStep: Record<string, string[]> = {
    basic: basicMissing,
    fiscal: fiscalMissing,
    'bank-accounts': bankMissing,
    training: trainingMissing,
    documents: pasoDocs ? pasoDocs.faltantes : documentsMissing,
  };

  const steps: OnboardingStep[] = [
    { id: 'basic', label: 'Identidad', isComplete: basicStageComplete, hasPartialData: basicStagePartial },
    ...stepDocumentos,
    { id: 'fiscal', label: 'Información fiscal', isComplete: fiscalStageComplete, hasPartialData: fiscalStagePartial },
    { id: 'bank-accounts', label: 'Cuenta bancaria', isComplete: bankComplete, hasPartialData: bankPendiente, hasCancelledData: bankRechazada, detalle: bankDetalle, tieneCaptura: bankTieneCuenta },
    { id: 'training', label: 'Capacitación', isComplete: trainingComplete, hasPartialData: trainingPartial, hasCancelledData: trainingCancelled },
  ];

  const completedCount = steps.filter(s => s.isComplete).length;
  const totalSteps = steps.length;

  const missingForComisiones: string[] = [];
  if (!basicStageComplete) missingForComisiones.push('Identidad');
  if (!documentsComplete) missingForComisiones.push('Documentos (INE/Carta)');
  if (!fiscalStageComplete) missingForComisiones.push('Información fiscal');
  if (!bankTieneCuenta) missingForComisiones.push('Cuenta bancaria');

  return {
    steps,
    completedCount,
    totalSteps,
    percentage: Math.round((completedCount / totalSteps) * 100),
    isLoading,
    hasTrainingComplete: trainingComplete,
    hasBasicIdentityComplete: basicStageComplete,
    canAccessComisiones: basicStageComplete && documentsComplete && fiscalStageComplete && bankTieneCuenta,
    missingForComisiones,
    missingByStep,
    esDependiente: !!hasInmobiliaria,
  };
}
